import { useEffect, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'

import SortIcon from '../../icons/sort.svg'

interface Props<
  SortOptionType extends string,
  OrderOptionType extends string,
  FilterOptionType extends string,
> {
  sort: SortOptionType
  setSort: (value: SortOptionType) => void
  sortOptions: { value: SortOptionType; label: string; disabled?: boolean }[]
  order: OrderOptionType
  setOrder: (value: OrderOptionType) => void
  orderOptions: { value: OrderOptionType; label: string; disabled?: boolean }[]
  filter?: FilterOptionType
  setFilter?: (value: FilterOptionType) => void
  filterOptions?: {
    value: FilterOptionType
    label: string
    disabled?: boolean
  }[]
  triggerIconClassName?: string
  extra?: React.ReactNode
  disabled?: boolean
  tooltip?: string
}

export const FilterSortOrderOptions = <
  SortOptionType extends string,
  OrderOptionType extends string,
  FilterOptionType extends string,
>({
  sort,
  setSort,
  sortOptions,
  order,
  setOrder,
  orderOptions,
  filter,
  setFilter,
  filterOptions,
  triggerIconClassName,
  extra,
  disabled,
  tooltip,
}: Props<SortOptionType, OrderOptionType, FilterOptionType>) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
    }
  }, [disabled])

  const isSort = (value: string): value is SortOptionType => {
    return sortOptions.some((option) => value === option.value)
  }

  const isOrder = (value: string): value is OrderOptionType => {
    return orderOptions.some((option) => value === option.value)
  }

  const isFilter = (value: string): value is FilterOptionType => {
    return (
      !!filterOptions && filterOptions.some((option) => value === option.value)
    )
  }

  const handleSetOrder = (order: string) => {
    if (isOrder(order)) {
      setOrder(order)
    }
  }

  const handleSetSort = (sort: string) => {
    if (isSort(sort)) {
      setSort(sort)
    }
  }

  const handleSetFilter = (filter: string) => {
    if (isFilter(filter) && setFilter) {
      setFilter(filter)
    }
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger
        className="outline-none"
        disabled={disabled}
        asChild
      >
        <IconButton
          icon={SortIcon}
          className={triggerIconClassName}
          disabled={disabled}
          tooltip={tooltip}
        />
      </PopoverTrigger>
      <PopoverContent className="z-100 w-[200px] p-2">
        {!!filterOptions && (
          <>
            <RadioGroup
              value={filter}
              onChange={handleSetFilter}
            >
              {filterOptions.map((option) => (
                <Radio
                  className="p-1.5"
                  key={option.value}
                  labelClassName="text-sm font-medium leading-[22px]"
                  disabled={option.disabled}
                  {...option}
                />
              ))}
            </RadioGroup>
            <div className="w-full border-b" />
          </>
        )}
        <RadioGroup
          value={sort}
          onChange={handleSetSort}
        >
          {sortOptions.map((option) => (
            <Radio
              className="p-1.5"
              key={option.value}
              labelClassName="text-sm font-medium leading-[22px]"
              disabled={option.disabled}
              {...option}
            />
          ))}
        </RadioGroup>
        <div className="w-full border-b" />
        <RadioGroup
          value={order}
          onChange={handleSetOrder}
        >
          {orderOptions.map((option) => (
            <Radio
              className="p-1.5"
              key={option.value}
              labelClassName="text-sm font-medium leading-[22px]"
              disabled={option.disabled}
              {...option}
            />
          ))}
        </RadioGroup>
        {extra}
      </PopoverContent>
    </Popover>
  )
}
